// Health page — temperature timeline + medication log. Lives outside /growth
// because temperature isn't modellable on growth curves and is most useful
// when sitting next to recent doses.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useMe } from '../api/hooks.ts';
import {
  useCreateMeasurement,
  useDeleteMeasurement,
  useMeasurements,
  useUpdateMeasurement,
} from '../api/measurements.ts';
import {
  useDeleteMedicationEvent,
  useMedicationEvents,
  useMedications,
} from '../api/medications.ts';
import { HeaderBar, HeaderIconButton } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { Section } from '../components/Section.tsx';
import { TemperatureChart } from '../components/health/TemperatureChart.tsx';
import { MeasurementSheet } from '../components/sheets/MeasurementSheet.tsx';
import { MedicationSheet } from '../components/sheets/MedicationSheet.tsx';
import { AnimatedNumber } from '../components/AnimatedNumber.tsx';
import {
  TimelineEntry,
  TimelineSpine,
  type SpineItem,
} from '../components/growth/TimelineSpine.tsx';
import { FEVER_THRESHOLD_C } from '@shared/constants.ts';
import type { Measurement, MedicationEvent } from '@shared/types.ts';
import { ageDaysAt, fmtAge } from '../lib/measure.ts';
import { fmtDur, relAgo, relTime } from '../lib/format.ts';

export function Health() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  const { data: allMeasurements = [] } = useMeasurements(baby?.id);
  const { data: medEvents = [] } = useMedicationEvents(baby?.id);
  const { data: meds = [] } = useMedications();

  const create = useCreateMeasurement();
  const update = useUpdateMeasurement();
  const del = useDeleteMeasurement();
  const delMedEvent = useDeleteMedicationEvent();

  const [addTempOpen, setAddTempOpen] = useState(false);
  const [editingTemp, setEditingTemp] = useState<Measurement | null>(null);
  const [addMedOpen, setAddMedOpen] = useState(false);
  const [editingMed, setEditingMed] = useState<MedicationEvent | null>(null);

  // ?add=temperature or ?add=medication from the Home picker opens the
  // appropriate drawer on arrival.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const add = params.get('add');
    if (add === 'temperature') setAddTempOpen(true);
    else if (add === 'medication') setAddMedOpen(true);
    if (add) {
      const next = new URLSearchParams(params);
      next.delete('add');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const temperatures = useMemo(
    () =>
      allMeasurements
        .filter((m) => m.kind === 'temperature')
        .sort((a, b) => b.occurred_at - a.occurred_at),
    [allMeasurements],
  );
  const latest = temperatures[0] ?? null;
  const now = Math.floor(Date.now() / 1000);
  const last24h = useMemo(
    () => temperatures.filter((t) => now - t.occurred_at <= 24 * 3600),
    [temperatures, now],
  );
  const maxIn24h =
    last24h.length > 0 ? Math.max(...last24h.map((t) => t.value)) / 10 : null;

  const medsById = useMemo(() => Object.fromEntries(meds.map((m) => [m.id, m])), [meds]);

  // Combined timeline: temperature readings + medication events.
  const spineItems = useMemo<SpineItem[]>(() => {
    const items: SpineItem[] = [];
    for (const t of temperatures) {
      items.push({
        key: `t:${t.id}`,
        at: t.occurred_at,
        accent: t.value >= FEVER_THRESHOLD_C * 10 ? 'var(--danger)' : 'var(--text-2)',
        render: () => (
          <TemperatureRow
            m={t}
            birthDate={baby?.birth_date ?? null}
            onClick={() => setEditingTemp(t)}
          />
        ),
      });
    }
    for (const ev of medEvents) {
      const med = medsById[ev.medication_id];
      items.push({
        key: `m:${ev.id}`,
        at: ev.occurred_at,
        accent: med?.color ?? 'var(--text-2)',
        render: () => (
          <MedRow
            ev={ev}
            medName={med?.name ?? 'Medication'}
            color={med?.color ?? null}
            birthDate={baby?.birth_date ?? null}
            onClick={() => setEditingMed(ev)}
          />
        ),
      });
    }
    return items;
  }, [temperatures, medEvents, medsById, baby?.birth_date]);

  const recentMed = medEvents[0] ?? null;
  const recentMedName = recentMed ? medsById[recentMed.medication_id]?.name : null;

  return (
    <>
      <HeaderBar
        baby={baby}
        title="Health"
        subtitle={baby ? `${baby.name} · ${fmtAge(ageDaysAt(baby.birth_date, now))}` : null}
        rightSlot={
          <HeaderIconButton ariaLabel="Add temperature" onClick={() => setAddTempOpen(true)}>
            <Icon.plus s={18} />
          </HeaderIconButton>
        }
      />
      <div className="lt-scroll" style={{ flex: 1, padding: '0 16px 24px' }}>
        {/* Sick mode banner */}
        {latest && now - latest.occurred_at <= 48 * 3600 && (
          <SickBanner
            latest={latest}
            maxIn24h={maxIn24h}
            recentMed={
              recentMed && recentMedName
                ? { name: recentMedName, occurredAt: recentMed.occurred_at }
                : null
            }
          />
        )}

        <Section title="Temperature">
          <div style={{ padding: '12px 12px 0' }}>
            <TemperatureChart readings={temperatures} birthDate={baby?.birth_date ?? null} />
            <div style={{ display: 'flex', gap: 8, padding: '8px 4px 12px' }}>
              <button
                type="button"
                className="lt-btn lt-btn-soft"
                style={{ flex: 1, padding: '10px 0', fontSize: 13 }}
                onClick={() => setAddTempOpen(true)}
              >
                <Icon.plus s={14} /> Add temperature
              </button>
              <button
                type="button"
                className="lt-btn lt-btn-soft"
                style={{ flex: 1, padding: '10px 0', fontSize: 13 }}
                onClick={() => setAddMedOpen(true)}
              >
                <Icon.plus s={14} /> Log medication
              </button>
            </div>
          </div>
        </Section>

        {spineItems.length > 0 ? (
          <div style={{ marginTop: 22 }}>
            <div
              style={{
                padding: '0 4px 10px',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.04,
              }}
            >
              Timeline
            </div>
            <TimelineSpine items={spineItems} scale="days" />
          </div>
        ) : (
          <div
            style={{
              marginTop: 22,
              padding: '32px 16px',
              borderRadius: 18,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              textAlign: 'center',
              color: 'var(--text-3)',
              fontSize: 13,
            }}
          >
            Nothing logged yet. Use the buttons above to record a temperature
            reading or medication dose.
          </div>
        )}
      </div>

      <MeasurementSheet
        open={addTempOpen}
        defaultKind="temperature"
        kinds={['temperature']}
        onClose={() => setAddTempOpen(false)}
        onSubmit={async (vars) => {
          try {
            await create.mutateAsync(vars);
            toast.success('Temperature saved');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
      />

      <MeasurementSheet
        open={!!editingTemp}
        existing={editingTemp}
        defaultKind="temperature"
        kinds={['temperature']}
        onClose={() => setEditingTemp(null)}
        onSubmit={async (vars) => {
          if (!editingTemp) return;
          try {
            await update.mutateAsync({
              id: editingTemp.id,
              patch: {
                kind: vars.kind,
                value: vars.value,
                occurred_at: vars.occurred_at,
                notes: vars.notes,
              },
            });
            toast.success('Saved');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
        onDelete={async () => {
          if (!editingTemp) return;
          try {
            await del.mutateAsync(editingTemp.id);
            toast.success('Deleted');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Delete failed');
          }
        }}
      />

      <MedicationSheet
        open={addMedOpen}
        onClose={() => setAddMedOpen(false)}
        babyId={baby?.id ?? null}
      />

      <MedicationSheet
        open={!!editingMed}
        onClose={() => setEditingMed(null)}
        babyId={baby?.id ?? null}
        existing={editingMed}
        onDelete={async () => {
          if (!editingMed) return;
          try {
            await delMedEvent.mutateAsync(editingMed.id);
            toast.success('Dose deleted');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}

function SickBanner({
  latest,
  maxIn24h,
  recentMed,
}: {
  latest: Measurement;
  maxIn24h: number | null;
  recentMed: { name: string; occurredAt: number } | null;
}) {
  const isFever = latest.value >= FEVER_THRESHOLD_C * 10;
  return (
    <div
      style={{
        marginBottom: 14,
        padding: '14px 16px',
        borderRadius: 18,
        background: isFever
          ? 'color-mix(in oklab, var(--danger) 12%, var(--bg-card))'
          : 'var(--bg-card)',
        border: isFever
          ? '1px solid color-mix(in oklab, var(--danger) 30%, transparent)'
          : '1px solid var(--border)',
        display: 'flex',
        alignItems: 'baseline',
        gap: 14,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: isFever ? 'var(--danger)' : 'var(--text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          {isFever ? 'Fever' : 'Latest'}
        </div>
        <AnimatedNumber
          value={latest.value / 10}
          format={(v) => `${v.toFixed(1)} °C`}
          className="lt-serif lt-tabular"
          style={{
            fontSize: 28,
            fontWeight: 400,
            letterSpacing: -0.015,
            color: 'var(--text-1)',
            display: 'inline-block',
          }}
        />
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
          {relAgo(latest.occurred_at)}
        </div>
      </div>
      <div style={{ flex: 1, textAlign: 'right', fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
        {maxIn24h != null && <div>max 24h · {maxIn24h.toFixed(1)} °C</div>}
        {recentMed && (
          <div>
            last dose · {recentMed.name} {fmtDur(Math.round((Date.now() / 1000 - recentMed.occurredAt) / 60))} ago
          </div>
        )}
      </div>
    </div>
  );
}

function TemperatureRow({
  m,
  birthDate,
  onClick,
}: {
  m: Measurement;
  birthDate: string | null;
  onClick: () => void;
}) {
  const isFever = m.value >= FEVER_THRESHOLD_C * 10;
  const age = birthDate ? fmtAge(ageDaysAt(birthDate, m.occurred_at)) : null;
  const d = new Date(m.occurred_at * 1000);
  return (
    <TimelineEntry
      onClick={onClick}
      hasNotes={!!m.notes}
      title={
        <span className="lt-tabular" style={{ display: 'inline-flex', gap: 6 }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>Temp</span>
          <span style={isFever ? { color: 'var(--danger)' } : undefined}>
            {(m.value / 10).toFixed(1)} °C
          </span>
          {isFever && (
            <span style={{ color: 'var(--danger)', fontSize: 11, fontWeight: 600 }}>fever</span>
          )}
        </span>
      }
      subtitle={
        <>
          {age && `${age} · `}
          {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
          {relTime(m.occurred_at)}
        </>
      }
    />
  );
}

function MedRow({
  ev,
  medName,
  color,
  birthDate,
  onClick,
}: {
  ev: MedicationEvent;
  medName: string;
  color: string | null;
  birthDate: string | null;
  onClick: () => void;
}) {
  const age = birthDate ? fmtAge(ageDaysAt(birthDate, ev.occurred_at)) : null;
  const d = new Date(ev.occurred_at * 1000);
  return (
    <TimelineEntry
      onClick={onClick}
      hasNotes={!!ev.notes}
      leading={
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: color ?? 'var(--text-3)',
            display: 'inline-block',
          }}
        />
      }
      title={
        <span className="lt-tabular" style={{ display: 'inline-flex', gap: 6 }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{medName}</span>
          <span>
            {ev.dose} {ev.unit}
          </span>
        </span>
      }
      subtitle={
        <>
          {age && `${age} · `}
          {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
          {relTime(ev.occurred_at)}
        </>
      }
    />
  );
}
